import { useMemo, useState, type ReactNode } from 'react';
import { Inbox, Search } from 'lucide-react';
import { ERRORS } from '@/constants';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCard, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { cn } from '@/lib/utils';
import { summarise, tabCounts, visibleLinks } from './logic';
import { useExtendLink, useResendLink, useReviewLinks, useRevokeLink } from './queries';
import { COLUMN_COUNT, ReviewLinkRow, type RowActions } from './ReviewLinkRow';
import { RevokeDialog } from './RevokeDialog';
import { SummaryCards } from './SummaryCards';
import type { ReviewLink, ReviewLinkTab } from './types';

/**
 * Every crown design the lab has sent to a dentist for review, in one place.
 *
 * The lab manages its own links - resend, extend, revoke - but only SEES the
 * patient link beside each one: that belongs to the dentist, who made it.
 */

const TABS: { key: ReviewLinkTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'awaiting', label: 'Awaiting review' },
  { key: 'changes_requested', label: 'Changes requested' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'closed', label: 'Expired or revoked' },
];

function TabBar({
  tab,
  counts,
  onChange,
}: {
  tab: ReviewLinkTab;
  counts: Record<ReviewLinkTab, number> | null;
  onChange: (tab: ReviewLinkTab) => void;
}) {
  return (
    <div role="tablist" aria-label="Filter links" className="flex flex-wrap gap-1">
      {TABS.map(({ key, label }) => {
        const selected = key === tab;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => {
              onChange(key);
            }}
            className={cn(
              'flex h-8 items-center gap-1.5 rounded-lg px-3 text-[12.5px] font-medium transition-colors',
              selected
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {label}
            {counts && (
              <span
                className={cn(
                  'rounded-full px-1.5 text-[10.5px] tabular-nums',
                  selected ? 'bg-white/20' : 'bg-muted',
                )}
              >
                {counts[key]}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** One full-width row with a message - empty, nothing matches, or failed. */
function MessageRow({ title, detail }: { title: string; detail: ReactNode }) {
  return (
    <tr className="border-b border-border">
      <td colSpan={COLUMN_COUNT} className="px-3 py-12 text-center">
        <Inbox className="mx-auto mb-2 size-6 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-1 text-[12.5px] text-muted-foreground">{detail}</p>
      </td>
    </tr>
  );
}

function SkeletonRows() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <tr key={i} className="border-b border-border">
          <td colSpan={COLUMN_COUNT} className="px-3 py-2.5">
            <Skeleton className="h-6 w-full bg-muted" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function ReviewLinks() {
  const links = useReviewLinks();
  const extend = useExtendLink();
  const resend = useResendLink();
  const revoke = useRevokeLink();

  const [tab, setTab] = useState<ReviewLinkTab>('all');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ReviewLink | null>(null);

  /*
   * One clock reading per fetch, so every row, tab count and card agrees on
   * what "now" is - and a row does not change state between two renders of
   * the same data. Refreshes whenever the list does, and is only read once
   * there is data - which is exactly when it is set.
   */
  const now = links.dataUpdatedAt;

  const all = links.data;
  const counts = useMemo(() => (all ? summarise(all, now) : null), [all, now]);
  const perTab = useMemo(() => (all ? tabCounts(all, now) : null), [all, now]);
  const rows = useMemo(
    () => (all ? visibleLinks(all, tab, query, now) : []),
    [all, tab, query, now],
  );

  const actions: RowActions = {
    onExtend: (link) => {
      extend.mutate(link.id);
    },
    onResend: (link) => {
      resend.mutate(link.id);
    },
    onRevoke: setRevoking,
  };

  let body: ReactNode;
  if (links.isPending) {
    body = <SkeletonRows />;
  } else if (links.isError) {
    body = (
      <MessageRow
        title="Review links could not be loaded"
        detail={links.error instanceof Error ? links.error.message : ERRORS.generic}
      />
    );
  } else if (all?.length === 0) {
    body = (
      <MessageRow title="No links here" detail="Designs sent for review from NuDent appear here." />
    );
  } else if (rows.length === 0) {
    body = <MessageRow title="No links here" detail="Nothing matches this filter." />;
  } else {
    body = rows.map((link) => (
      <ReviewLinkRow
        key={link.id}
        link={link}
        now={now}
        expanded={link.id === expandedId}
        onToggle={() => {
          setExpandedId((current) => (current === link.id ? null : link.id));
        }}
        actions={actions}
      />
    ));
  }

  return (
    <div className="space-y-4">
      <SummaryCards counts={counts} />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <TabBar tab={tab} counts={perTab} onChange={setTab} />
        <div className="relative lg:ml-auto lg:w-72">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder="Patient ID, tooth or dentist"
            aria-label="Search links"
            className="pl-9"
          />
        </div>
      </div>

      <TableCard>
        <Table aria-busy={links.isPending}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Patient</TableHead>
              <TableHead>Dentist</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Link</TableHead>
              <TableHead>Opened</TableHead>
              <TableHead>Decision</TableHead>
              <TableHead>Patient link</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{body}</TableBody>
        </Table>
      </TableCard>

      <RevokeDialog
        link={revoking}
        pending={revoke.isPending}
        onCancel={() => {
          setRevoking(null);
        }}
        onConfirm={(link) => {
          revoke.mutate(link.id, {
            onSettled: () => {
              setRevoking(null);
            },
          });
        }}
      />
    </div>
  );
}
